import { useId } from 'react';
import type { ItemId, OptionId, TargetId } from '../core/model/brands.ts';
import { brandAs } from '../core/model/brands.ts';
import type { Response } from '../core/model/session.ts';
import { useI18n } from '../i18n/index.tsx';
import { Button } from '../ui/primitives.tsx';
import {
  OptionLabel,
  optionState,
  orderOptions,
  type RendererProps,
} from './shared/QuestionParts.tsx';
import { ExamText } from '../ui/primitives.tsx';

/**
 * How each question type is answered.
 *
 * The rule that shapes all five: **everything is operable from the keyboard, with no exceptions and
 * no fallbacks.** Matching uses a native `<select>` per item; ordering uses move buttons. Neither
 * offers drag-and-drop, and that is not a gap to fill later — a keyboard-first control that also
 * works with a mouse is simply a better control than a drag surface with a keyboard alternative
 * bolted on, and it is the one that gets tested every time anyone uses the app.
 */

export function SingleChoiceRenderer({
  question,
  optionOrder,
  response,
  onChange,
  disabled,
  revealed,
}: RendererProps) {
  const groupName = useId();
  if (question.type !== 'single-choice') {
    return null;
  }

  const options = orderOptions(question.interaction.options, optionOrder);
  const chosen = response?.type === 'single-choice' ? [response.optionId] : [];
  const correct = [question.interaction.correctOptionId];

  return (
    <fieldset className="options" disabled={disabled}>
      <legend className="visually-hidden">{question.content.prompt}</legend>

      {options.map((option, index) => {
        const state = optionState(option.id, correct, chosen, revealed);
        return (
          <label key={option.id} className={`option option--${state}`}>
            <input
              type="radio"
              name={groupName}
              value={option.id}
              checked={chosen.includes(option.id)}
              onChange={() => {
                onChange({ type: 'single-choice', optionId: option.id });
              }}
            />
            <OptionLabel option={option} index={index} lang={question.content.language} />
            <OptionFeedback option={option} revealed={revealed} lang={question.content.language} />
          </label>
        );
      })}
    </fieldset>
  );
}

export function MultipleChoiceRenderer({
  question,
  optionOrder,
  response,
  onChange,
  disabled,
  revealed,
}: RendererProps) {
  const { t } = useI18n();
  if (question.type !== 'multiple-choice') {
    return null;
  }

  const options = orderOptions(question.interaction.options, optionOrder);
  const chosen = response?.type === 'multiple-choice' ? response.optionIds : [];
  const { selection } = question.interaction;

  const toggle = (optionId: OptionId): void => {
    const next = chosen.includes(optionId)
      ? chosen.filter((id) => id !== optionId)
      : [...chosen, optionId];
    onChange(next.length === 0 ? null : { type: 'multiple-choice', optionIds: next });
  };

  return (
    <fieldset className="options" disabled={disabled}>
      <legend className="options__legend">
        {selection.min === selection.max
          ? t('session.selectCount', { count: selection.max })
          : t('session.selectRange', { min: selection.min, max: selection.max })}
      </legend>

      {options.map((option, index) => {
        const state = optionState(option.id, question.interaction.correctOptionIds, chosen, revealed);
        const atLimit = chosen.length >= selection.max && !chosen.includes(option.id);

        return (
          <label key={option.id} className={`option option--${state}`}>
            <input
              type="checkbox"
              checked={chosen.includes(option.id)}
              // Disabled rather than silently ignored, so the limit is visible before it bites.
              disabled={disabled || atLimit}
              onChange={() => {
                toggle(option.id);
              }}
            />
            <OptionLabel option={option} index={index} lang={question.content.language} />
            <OptionFeedback option={option} revealed={revealed} lang={question.content.language} />
          </label>
        );
      })}
    </fieldset>
  );
}

/**
 * Matching, as one `<select>` per item.
 *
 * A native select is keyboard-operable, screen-reader-announced and touch-friendly on every
 * platform, for free. Everything a drag surface would have to reimplement, it already does.
 */
export function MatchingRenderer({
  question,
  optionOrder,
  response,
  onChange,
  disabled,
  revealed,
}: RendererProps) {
  const { t } = useI18n();
  const baseId = useId();
  if (question.type !== 'matching') {
    return null;
  }

  const targets = orderOptions(question.interaction.targets, optionOrder);
  const pairs = response?.type === 'matching' ? response.pairs : [];
  const chosenFor = (itemId: ItemId): string =>
    pairs.find((pair) => pair.itemId === itemId)?.targetId ?? '';

  const setPair = (itemId: ItemId, targetId: string): void => {
    const others = pairs.filter((pair) => pair.itemId !== itemId);
    const next =
      targetId === ''
        ? others
        : [...others, { itemId, targetId: brandAs<TargetId>(targetId) }];
    onChange(next.length === 0 ? null : { type: 'matching', pairs: next });
  };

  const correctFor = (itemId: ItemId): string | undefined =>
    question.interaction.pairs.find((pair) => pair.itemId === itemId)?.targetId;

  return (
    <div className="matching">
      {question.interaction.items.map((item) => {
        const itemId = brandAs<ItemId>(item.id);
        const chosen = chosenFor(itemId);
        const expected = correctFor(itemId);
        const state = !revealed ? 'neutral' : chosen === expected ? 'correct' : 'incorrect';

        return (
          <div key={item.id} className={`matching__row matching__row--${state}`}>
            <label className="matching__item" htmlFor={`${baseId}-${item.id}`}>
              <ExamText as="span" lang={question.content.language}>
                {item.text}
              </ExamText>
            </label>

            <select
              id={`${baseId}-${item.id}`}
              className="matching__select"
              value={chosen}
              disabled={disabled}
              onChange={(event) => {
                setPair(itemId, event.target.value);
              }}
            >
              <option value="">{t('session.chooseTarget')}</option>
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.text}
                </option>
              ))}
            </select>

            {revealed && expected !== undefined && chosen !== expected && (
              <p className="matching__answer">
                {t('results.correctAnswer')}:{' '}
                {targets.find((target) => (target.id as string) === expected)?.text ?? expected}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Ordering, as a list with move buttons.
 *
 * Each move announces the new position through the button's own label, so a screen-reader user
 * always knows where the item landed — the piece of feedback that drag-and-drop makes hardest to
 * provide and easiest to forget.
 */
export function OrderingRenderer({
  question,
  optionOrder,
  response,
  onChange,
  disabled,
  revealed,
}: RendererProps) {
  const { t } = useI18n();
  if (question.type !== 'ordering') {
    return null;
  }

  const initial = orderOptions(question.interaction.items, optionOrder).map((item) =>
    brandAs<ItemId>(item.id),
  );
  const current = response?.type === 'ordering' && response.order.length > 0 ? response.order : initial;
  // Ordering items are branded ItemId while the option list is branded OptionId; both are the same
  // string in the file, so the comparison is on the underlying value.
  const labelOf = (id: ItemId): string =>
    question.interaction.items.find((item) => (item.id as string) === (id as string))?.text ?? id;

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= current.length) {
      return;
    }
    const next = [...current];
    const [moved] = next.splice(from, 1);
    if (moved !== undefined) {
      next.splice(to, 0, moved);
    }
    onChange({ type: 'ordering', order: next });
  };

  return (
    <ol className="ordering">
      {question.interaction.orderLabel?.first !== undefined && (
        <li className="ordering__edge" aria-hidden="true">
          {question.interaction.orderLabel.first}
        </li>
      )}

      {current.map((id, index) => {
        const state = !revealed
          ? 'neutral'
          : question.interaction.correctOrder[index] === id
            ? 'correct'
            : 'incorrect';

        return (
          <li key={id} className={`ordering__item ordering__item--${state}`}>
            <span className="ordering__position" aria-hidden="true">
              {index + 1}
            </span>
            <ExamText as="span" lang={question.content.language} className="ordering__text">
              {labelOf(id)}
            </ExamText>

            <span className="ordering__controls">
              <Button
                variant="quiet"
                disabled={disabled || index === 0}
                aria-label={`${t('session.moveUp')}: ${labelOf(id)}`}
                onClick={() => {
                  move(index, index - 1);
                }}
              >
                ↑
              </Button>
              <Button
                variant="quiet"
                disabled={disabled || index === current.length - 1}
                aria-label={`${t('session.moveDown')}: ${labelOf(id)}`}
                onClick={() => {
                  move(index, index + 1);
                }}
              >
                ↓
              </Button>
            </span>
          </li>
        );
      })}

      {question.interaction.orderLabel?.last !== undefined && (
        <li className="ordering__edge" aria-hidden="true">
          {question.interaction.orderLabel.last}
        </li>
      )}
    </ol>
  );
}

export function TextRenderer({ question, response, onChange, disabled, revealed }: RendererProps) {
  const { t } = useI18n();
  const inputId = useId();
  if (question.type !== 'text') {
    return null;
  }

  const value = response?.type === 'text' ? response.text : '';
  const update = (next: string): void => {
    onChange(next === '' ? null : { type: 'text', text: next });
  };

  return (
    <div className="text-answer">
      <label htmlFor={inputId}>{t('session.answerHere')}</label>

      {question.interaction.inputMode === 'multi-line' ? (
        <textarea
          id={inputId}
          rows={6}
          maxLength={question.interaction.maxLength}
          value={value}
          disabled={disabled}
          onChange={(event) => {
            update(event.target.value);
          }}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          maxLength={question.interaction.maxLength}
          value={value}
          disabled={disabled}
          onChange={(event) => {
            update(event.target.value);
          }}
        />
      )}

      {revealed && question.interaction.evaluation === 'auto' && (
        <p className="text-answer__accepted">
          {t('results.correctAnswer')}: {question.interaction.accepted.join(' · ')}
        </p>
      )}

      {revealed && question.interaction.sampleAnswer !== undefined && (
        <div className="text-answer__sample">
          <p className="text-answer__sampleTitle">{t('results.correctAnswer')}</p>
          <ExamText lang={question.content.language}>{question.interaction.sampleAnswer}</ExamText>
        </div>
      )}
    </div>
  );
}

function OptionFeedback({
  option,
  revealed,
  lang,
}: {
  readonly option: { readonly feedback?: string };
  readonly revealed: boolean;
  readonly lang: string;
}) {
  if (!revealed || option.feedback === undefined) {
    return null;
  }
  return (
    <ExamText lang={lang} className="option__feedback">
      {option.feedback}
    </ExamText>
  );
}

export type { Response };
